import { z } from 'zod'
import {
  greetingCardDecorationAssetSchema,
  greetingCardDecorationSchema,
  greetingCardDecorationSourceSchema,
  greetingCardDecorationsBySideSchema,
  greetingCardDecorationTransformSchema,
  greetingCardFormatSchema,
  greetingCardPageSideSchema,
  greetingCardSessionAssetReferenceSchema,
} from './schemas'

/** Identifies which printable side of the greeting card a shopper decoration belongs to. */
export { greetingCardPageSideSchema }

/** Identifies how a shopper-added decoration should be rendered or regenerated. */
export { greetingCardDecorationSourceSchema }

/**
 * Private object-storage reference for shopper-uploaded card assets.
 *
 * The bucket is intentionally not part of the payload. Each host app owns the
 * bucket selection for its environment and should resolve it in code.
 */
export { greetingCardSessionAssetReferenceSchema }

/** Position, size, rotation, and stacking order for a shopper decoration. */
export { greetingCardDecorationTransformSchema }

/**
 * Serializable decoration asset sent from the builder to a host.
 *
 * QR decorations persist semantic data instead of a generated QR image; the
 * host can regenerate the visual from `qrTargetUrl` during render.
 */
export const greetingCardPayloadDecorationAssetSchema = greetingCardDecorationAssetSchema

/** Serializable shopper decoration attached to either the outside or inside card surface. */
export const greetingCardPayloadDecorationSchema = greetingCardDecorationSchema

/** Shopper decorations grouped by printable side. */
export const greetingCardPayloadDecorationsBySideSchema = greetingCardDecorationsBySideSchema

/** Shopper photo transform inside the selected template photo slot. */
export const greetingCardPhotoPlacementSchema = z.object({
  scale: z.number(),
  rotationDeg: z.number(),
  offsetX: z.number(),
  offsetY: z.number(),
}).strict()

/** Downloadable font face used to reproduce the shopper's font at print time. */
export const greetingCardInsideFontFaceSchema = z.object({
  src: z.string().url(),
  weight: z.number(),
  style: z.enum(['normal', 'italic']),
}).strict()

/**
 * Where the inside message font comes from, so the print render can load the
 * exact same font the builder measured with. Google fonts are fetched from the
 * css2 API by family name; custom fonts carry absolute face URLs.
 */
export const greetingCardInsideFontSourceSchema = z.object({
  type: z.enum(['google', 'custom']),
  family: z.string().min(1),
  faces: z.array(greetingCardInsideFontFaceSchema).optional(),
}).strict()

/** Shopper-authored inside message and typography choices. */
export const greetingCardInsideTextSchema = z.object({
  text: z.string(),
  fontFamily: z.string(),
  fontSizePt: z.number(),
  resolvedFontSizePt: z.number().optional(),
  resolvedFontSizeRatio: z.number().optional(),
  fontSource: greetingCardInsideFontSourceSchema.optional(),
  colorHex: z.string(),
  align: z.enum(['left', 'center', 'right']),
  weight: z.enum(['regular', 'bold']),
  vAlign: z.enum(['top', 'middle', 'bottom']).optional(),
  maxChars: z.number(),
}).strict()

/** Video or audio message attached to the printed card through a QR decoration. */
export const greetingCardAttachmentSchema = z.object({
  type: z.enum(['video', 'audio']),
  code: z.string().min(1),
  playbackUrl: z.string().optional(),
}).strict()

/**
 * Shopper photo payload before or after host-side asset normalization.
 *
 * The builder sends `imageUrl` for fresh local data URLs. The Shopify host may
 * replace that with `asset` after uploading the image to private storage.
 */
export const greetingCardSessionPhotoSchema = z.object({
  imageUrl: z.string().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
  placement: greetingCardPhotoPlacementSchema.optional(),
  asset: greetingCardSessionAssetReferenceSchema.optional(),
  effectiveDpi: z.number().optional(),
}).strict()

/**
 * Canonical saved-card payload sent by the greeting-card builder and consumed
 * by hosts such as the Shopify app.
 */
export const greetingCardSessionPayloadSchema = z.object({
  schemaVersion: z.number().optional(),
  designId: z.string().min(1),
  variantId: z.string().min(1),
  designType: z.enum(['static', 'photo']),
  cardFormat: greetingCardFormatSchema.optional(),
  frontTextLayers: z.record(z.string(), z.string()).optional(),
  frontTextResolvedFontSizeRatios: z.record(z.string(), z.number()).optional(),
  inside: greetingCardInsideTextSchema,
  decorations: greetingCardPayloadDecorationsBySideSchema.optional(),
  attachment: greetingCardAttachmentSchema.optional(),
  playbackBaseUrl: z.string().optional(),
  photo: greetingCardSessionPhotoSchema.optional(),
}).strict()

export type GreetingCardPayloadDecorationAsset = z.infer<typeof greetingCardPayloadDecorationAssetSchema>
export type GreetingCardPayloadDecoration = z.infer<typeof greetingCardPayloadDecorationSchema>
export type GreetingCardPayloadDecorationsBySide = z.infer<typeof greetingCardPayloadDecorationsBySideSchema>
export type GreetingCardPhotoPlacement = z.infer<typeof greetingCardPhotoPlacementSchema>
export type GreetingCardInsideFontFace = z.infer<typeof greetingCardInsideFontFaceSchema>
export type GreetingCardInsideFontSource = z.infer<typeof greetingCardInsideFontSourceSchema>
export type GreetingCardInsideText = z.infer<typeof greetingCardInsideTextSchema>
export type GreetingCardAttachment = z.infer<typeof greetingCardAttachmentSchema>
export type GreetingCardSessionPhoto = z.infer<typeof greetingCardSessionPhotoSchema>
export type GreetingCardSessionPayload = z.infer<typeof greetingCardSessionPayloadSchema>
